// DEPENDENCIES
import { FormikValues } from "formik";

// TYPES
import { Field, MidgardQuestions, PersonType, Step } from "../../types";

// UTILS
import { getDraftStepsToSend, isDev } from "../../app/utils";
import { getCompanyData } from "../../utils/companies";
import { sendStep } from "../../connectors/connectors";
import { addSendedStep } from "../../reducers/onboardingDataReducer";
import { radioOrSingleSelectRequired } from "../../validations";
import {
  getQualifiedInvestorField,
  getQualifiedInvestorType,
} from "../shared/qualifiedInvestorField";

const investmentProfileJuridicalStep = (
  questions: MidgardQuestions,
): Omit<Step, "step"> => {
  const formattedQuestions = questionsToFields(questions);

  return {
    name: "Perfil inversor",
    subtitle: "El riesgo a tomar según el tipo de inversiones.",
    fields: [
      ...formattedQuestions.filter((_, index) => index < 5),
      ...getQualifiedInvestorField(),
      ...formattedQuestions.filter((_, index) => index >= 5),
    ] as Field[],
    onContinue: async (values, dispatch, state) => {
      if (!state || !dispatch)
        throw new Error("Error: state or dispatch not found");

      const { email } = values;
      const {
        onboardingData: { sendedSteps },
      } = state;

      const step = getParsedStep(questions, values);

      const stepsWithStepFive = getDraftStepsToSend(sendedSteps, step);

      const body = {
        companyType: getCompanyData().companyName,
        personType: state.activeTemplate.onboardingType as PersonType,
        email,
        steps: stepsWithStepFive,
      };
      await sendStep({ stepId: 5, body });
      dispatch(addSendedStep(step));
    },
  };
};

const TWO_COLUMN_QUESTIONS = [
  "Objetivo de inversión",
  "Horizonte de inversión previsto",
];

const questionsToFields = (questions: MidgardQuestions) =>
  questions.map((question) => {
    const options = question.choices.map((choice) => {
      return { value: choice.option, label: choice.option };
    });

    const isRiskProfileQuestion =
      question.externalCode === questionsUUIDs.riskProfile;

    return {
      name: `investmentProfile.${question.externalCode}`,
      type: "select",
      label: isRiskProfileQuestion ? "Riesgo a asumir" : question.text,
      placeholder: "Seleccioná una opción",
      validation: radioOrSingleSelectRequired(options),
      options,
      tooltip: isRiskProfileQuestion
        ? {
            label: "Ver tabla de riesgo",
            modal: "modal-risk-profile",
          }
        : undefined,
      selectType: "single",
      initialValue: "",
      columns: TWO_COLUMN_QUESTIONS.includes(question.text) ? 2 : 1,
    };
  }) as Field[];

const getParsedStep = (questions: MidgardQuestions, values: FormikValues) => {
  const { investmentProfile } = values;

  const choices = Object.keys(investmentProfile);

  const choicesParsed = choices
    .map(
      (choice) =>
        findExternalCodeOfValue(questions, choice, values)?.externalCode,
    )
    .filter((code): code is string => code !== undefined);

  return {
    "@type": "5_JURIDICAL",
    investmentProfile: {
      choices: [...choicesParsed],
    },
    qualifiedInvestorType: getQualifiedInvestorType(values),
  };
};

// Find the external code of the value (the answer of question)
// Each question has a externalCode and each answer too, so we need to find the externalCode of the answer about the question
const findExternalCodeOfValue = (
  questions: MidgardQuestions,
  externalCode: string,
  values: FormikValues,
) => {
  const question = questions.find(
    (question) => question.externalCode === externalCode,
  );
  return question?.choices.find(
    (choice) => choice.option === values.investmentProfile[externalCode],
  );
};

const devQuestionsUUIDs = {
  riskProfile: "1f794542-ccb7-4128-892b-26f3a0c676e4",
};

const prodQuestionsUUIDs = {
  riskProfile: "30d210d5-189c-4033-a694-dcf65a254bfd",
};

const questionsUUIDs = isDev ? devQuestionsUUIDs : prodQuestionsUUIDs;

export default investmentProfileJuridicalStep;
